import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/format";
import { ArrowLeft, FileText, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/cuenta/pedidos/$id")({
  component: DetallePedido,
});

const STATUS_LABELS: Record<string, string> = {
  nuevo: "Pago pendiente",
  pagado: "Pagado",
  intake_pendiente: "Falta tu formulario",
  en_proceso: "En proceso",
  esperando_cliente: "Esperando tus datos",
  completado: "Completado",
  cancelado: "Cancelado",
};

function DetallePedido() {
  const { id } = Route.useParams();
  const [order, setOrder] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [intake, setIntake] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [o, it, fm] = await Promise.all([
        supabase.from("orders").select("id,status,total,created_at,intake_completed,intake_token").eq("id", id).maybeSingle(),
        supabase.from("order_items").select("name_snapshot,price_snapshot,qty").eq("order_id", id),
        supabase.from("intake_forms").select("submitted_at").eq("order_id", id).maybeSingle(),
      ]);
      setOrder(o.data);
      setItems(it.data ?? []);
      setIntake(fm.data);
      setLoading(false);
    })();
  }, [id]);

  if (loading) return <div className="text-muted-foreground">Cargando...</div>;
  if (!order) return <div className="text-muted-foreground">Pedido no encontrado o sin acceso.</div>;

  const paid = order.status !== "nuevo";
  const needsIntake = paid && !order.intake_completed;

  return (
    <div className="space-y-6">
      <Link to="/cuenta" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> Volver a mis pedidos
      </Link>

      <div className="rounded-xl border border-hairline bg-surface p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-label-accent">Pedido</div>
            <h1 className="mt-1 font-display text-2xl font-semibold text-foreground">#{order.id.slice(0, 8)}</h1>
            <div className="mt-1 text-xs text-muted-foreground">{new Date(order.created_at).toLocaleString()}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground">Estado</div>
            <div className="mt-1 font-medium text-foreground">{STATUS_LABELS[order.status] ?? order.status}</div>
          </div>
        </div>

        <div className="mt-6 space-y-2 border-t border-hairline pt-4">
          {items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-foreground">{it.name_snapshot}</span>
              <span className="text-muted-foreground">{money(it.price_snapshot)}</span>
            </div>
          ))}
          <div className="mt-3 flex justify-between border-t border-hairline pt-3 font-semibold text-foreground">
            <span>Total</span><span>{money(order.total)}</span>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-hairline bg-surface p-6">
        <h2 className="font-display text-lg font-semibold text-foreground">Próximos pasos</h2>

        {!paid && (
          <p className="mt-3 text-sm text-muted-foreground">Tu pago aún no fue confirmado. Si ya pagaste, espera unos minutos.</p>
        )}

        {needsIntake && order.intake_token && (
          <div className="mt-4 flex flex-col gap-3 rounded-md border border-primary/40 bg-primary/10 p-4">
            <div>
              <div className="font-medium text-foreground">Completa el formulario de datos del pedido</div>
              <p className="mt-1 text-xs text-muted-foreground">Con esos datos arrancamos el proceso.</p>
            </div>
            <Link to="/intake/$token" params={{ token: order.intake_token }}>
              <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <FileText className="mr-2 h-4 w-4" /> Completar formulario
              </Button>
            </Link>
          </div>
        )}

        {order.intake_completed && intake && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-success/40 bg-success/10 p-4 text-sm text-foreground">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
            <div>
              Formulario enviado el {new Date(intake.submitted_at).toLocaleString()}. Estamos trabajando en tu pedido.
            </div>
          </div>
        )}

        {order.status === "completado" && (
          <p className="mt-3 text-sm text-foreground">Tu pedido está completado. Si necesitas algo más, contáctanos por WhatsApp.</p>
        )}
      </div>
    </div>
  );
}
